// Resume Content (stored in Report.content JSONB)
export interface ResumeContent {
  personalInfo: ResumePersonalInfo;
  profile: ResumeProfile;
  contact: ResumeContact;
  links: ResumeLink[];
  skills: ResumeSkills;
  employment: ResumeEmployment[];
  education: ResumeEducation[];
  certifications: ResumeCertification[];
  customSections: ResumeCustomSection[];
  /**
   * Data-processing (GDPR) consent clause, printed as fine print after every
   * other section. Optional: resumes saved before the feature existed simply
   * have no key, and must keep rendering exactly as they did.
   */
  consent?: ResumeConsent;
}

// Resume Settings (stored in Report.settings JSONB)
export interface ResumeSettings {
  templateId: string;
  colorSchemeId: string;
  originalResumeSourceId?: string;
  refinement?: {
    isRefined: boolean;
    status?: 'PENDING' | 'COMPLETED' | 'FAILED';
    refinedAt?: string;
    sourceResumeId?: string;
    sourceResumeType?: 'builder' | 'uploaded';
    contextSummary?: string;
    contextItems?: Array<{
      type: 'JOB' | 'COMPANY' | 'NOTE' | 'RAW' | 'PROFILE' | 'FILE' | 'RESUME';
      title: string;
    }>;
  };
}

export interface ResumeShare {
  enabled: boolean;
  path: string | null;
}

// Full Resume Data (combines Report fields + content + settings)
export interface ResumeData {
  id: string;
  name: string;
  version: number;
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
  parentReportId?: string;
  content: ResumeContent;
  settings: ResumeSettings;
  share?: ResumeShare;
  sources?: ResumeSource[];
  originalSourceId?: string;
  originalSourceTitle?: string;
  originalSourceFileName?: string;
  originalSourceDownloadUrl?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ResumeSource {
  id: string;
  sourceId: string;
  type: 'JOB' | 'COMPANY' | 'RESUME';
  title: string;
}

// Sub-types for content sections
export interface ResumePersonalInfo {
  firstName: string;
  lastName: string;
  profilePictureUrl?: string;
}

export interface ResumeProfile {
  headline?: string;
  summary?: string;
}

export interface ResumeContact {
  location?: string;
  phone?: string;
  email?: string;
}

export type ResumeLinkType = 'linkedin' | 'portfolio' | 'github' | 'twitter' | 'custom';

export interface ResumeLink {
  id: string;
  type: ResumeLinkType;
  url: string;
  label?: string;
}

export interface ResumeSkills {
  technical: string[];
  soft: string[];
  categories: ResumeSkillCategory[];
}

export interface ResumeSkillCategory {
  id: string;
  name: string;
  skills: string[];
}

export interface ResumeEmployment {
  id: string;
  title: string;
  company: string;
  location?: string;
  startDate: string;
  endDate?: string;
  isCurrentlyWorking: boolean;
  responsibilities: string; // Rich text HTML
}

export interface ResumeEducation {
  id: string;
  institution: string;
  degree: string;
  field?: string;
  startDate?: string;
  endDate?: string;
  gpa?: string;
}

export interface ResumeCertification {
  id: string;
  name: string;
  issuer: string;
  date?: string;
  issueDate?: string;
  expiryDate?: string;
  credentialUrl?: string;
}

export interface ResumeConsent {
  /** Whether the clause is printed on the resume. */
  enabled: boolean;
  /**
   * The clause itself, as plain text. Held verbatim — on import this is the
   * candidate's own wording, which may cite national law we must not replace.
   * See `lib/resume/consent.ts` for the default used when a user opts in.
   */
  text: string;
}

export interface ResumeCustomSection {
  id: string;
  title: string;
  content: string; // Rich text HTML
  order: number;
}

// Template types
export interface ResumeTemplate {
  id: string;
  name: string;
  description: string;
  thumbnail: string;
  /**
   * Whether this template lays out a profile photo. Declared here rather than
   * inferred at render time: several ids share one Handlebars source, and
   * whether a photo belongs is a property of the *template*, not the markup —
   * `ats-optimized` shares nothing with `minimal` yet both omit it on purpose.
   */
  supportsProfilePhoto?: boolean;
}

// Color Scheme types
export interface ResumeColorScheme {
  id: string;
  name: string;
  templateIds?: string[];
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    text: string;
    background: string;
    muted: string;
  };
  templateOverrides?: Record<string, Record<string, string>>;
}

export type ExportFormat = 'pdf' | 'docx' | 'txt';

// API request/response types
export interface CreateResumeRequest {
  name: string;
  content?: Partial<ResumeContent>;
  settings?: Partial<ResumeSettings>;
}

export interface UpdateResumeRequest {
  name?: string;
  content?: Partial<ResumeContent>;
  settings?: Partial<ResumeSettings>;
  status?: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
}

export interface ImproveTextRequest {
  text: string;
  field: 'summary' | 'responsibilities' | 'custom';
  instructions?: string;
  context?: {
    jobTitle?: string;
    company?: string;
  };
}

export interface ImproveTextResponse {
  improvedText: string;
}

export interface ExportResumeRequest {
  format: ExportFormat;
  options?: {
    includePhoto?: boolean;
  };
}

export interface ExportResumeResponse {
  data: string; // base64 for PDF/DOCX, plain text for TXT
  filename: string;
  mimeType: string;
}

// Default values
export const DEFAULT_RESUME_CONTENT: ResumeContent = {
  personalInfo: {
    firstName: '',
    lastName: '',
  },
  profile: {},
  contact: {},
  links: [],
  skills: {
    technical: [],
    soft: [],
    categories: [],
  },
  employment: [],
  education: [],
  certifications: [],
  customSections: [],
  consent: {
    enabled: false,
    text: '',
  },
};

export const DEFAULT_RESUME_SETTINGS: ResumeSettings = {
  templateId: 'professional',
  colorSchemeId: 'charcoal',
};
